import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, HelpCircle, X } from "lucide-react";
import React, { useState } from "react";
import { cn } from "@repo/core";
import { quickStartFiles } from "../lib/quickstart/content";
import { useT } from "../i18n/useT";

const FAQ_KEYS = [0, 1, 2, 3, 4, 5, 6, 7] as const;

interface Props {
  open: boolean;
  onClose: () => void;
  onReplayTour?: () => void;
}

export function HelpModal({ open, onClose, onReplayTour }: Props) {
  const { t, locale } = useT();
  const [openIdx, setOpenIdx] = useState<number | null>(0);
  const guides = quickStartFiles(locale);

  const guideLinks = (
    <div className="space-y-2">
      <a
        href={`./${guides.pdf}`}
        target="_blank"
        rel="noopener noreferrer"
        className="app-btn-outline min-h-[44px] inline-flex items-center justify-center text-sm px-4"
      >
        {t("help.quickStart")}
      </a>
      <a
        href={`./${guides.html}`}
        target="_blank"
        rel="noopener noreferrer"
        className="text-[11px] text-muted-foreground underline underline-offset-2 block py-1"
      >
        {t("help.quickStartHtml")}
      </a>
    </div>
  );

  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={onClose}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.96 }}
            className="relative z-10 w-full max-w-lg max-h-[88vh] flex flex-col bg-card border border-border rounded-2xl shadow-2xl overflow-hidden"
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-border shrink-0">
              <div className="flex items-center gap-2">
                <HelpCircle size={18} className="text-primary" />
                <h2 className="font-semibold text-foreground">{t("help.title")}</h2>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="min-h-[44px] min-w-[44px] inline-flex items-center justify-center rounded-lg hover:bg-muted"
                aria-label={t("help.close")}
              >
                <X size={16} />
              </button>
            </div>

            <div className="overflow-y-auto flex-1 p-2">
              {FAQ_KEYS.map((i) => {
                const isOpen = openIdx === i;
                return (
                  <div key={i} className="border-b border-border last:border-0">
                    <button
                      type="button"
                      onClick={() => setOpenIdx(isOpen ? null : i)}
                      className="w-full flex items-start gap-2 px-3 py-3 text-left min-h-[44px]"
                    >
                      <ChevronDown
                        size={16}
                        className={cn(
                          "shrink-0 mt-0.5 text-muted-foreground transition-transform",
                          isOpen && "rotate-180"
                        )}
                      />
                      <span className="text-sm font-medium text-foreground">{t(`help.q${i}`)}</span>
                    </button>
                    {isOpen && (
                      <p className="px-3 pb-3 pl-9 text-xs text-muted-foreground leading-relaxed">
                        {t(`help.a${i}`)}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="p-4 border-t border-border shrink-0 space-y-2">
              {onReplayTour && (
                <button type="button" className="app-btn min-h-[44px]" onClick={onReplayTour}>
                  {t("help.replayTour")}
                </button>
              )}
              {guideLinks}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}

export function HelpFab({ onClick }: { onClick: () => void }) {
  const { t } = useT();
  return (
    <button
      type="button"
      onClick={onClick}
      className="fixed bottom-20 left-5 lg:bottom-6 lg:left-6 z-40 rounded-full bg-card border border-border shadow-lg hover:scale-105 active:scale-95 transition-transform flex items-center justify-center no-print text-primary"
      aria-label={t("app.help")}
      style={{ width: "3rem", height: "3rem" }}
    >
      <HelpCircle size={20} />
    </button>
  );
}
